import React, { useRef, useCallback, useState, useEffect } from 'react';
import { EditableField } from './EditableField';
import type { Row, MatchInfo } from '../state';
import { SPOT_PRODUCT_CODE, slipInfo, subtotalLabelOf } from '../state';
import { CodeSuggestInput } from './CodeSuggestInput';

interface Props {
  rows: Row[];
  matches: Record<string, MatchInfo>;
  matchesOriginal: Record<string, MatchInfo>;
  confirmedRows: Set<string>;
  isCellEdited: (rowId: string, field: string) => boolean;
  onCellEdit: (rowId: string, field: keyof Row, value: string) => void;
  onRowDelete: (rowId: string) => void;
  onRowRemove: (rowId: string) => void;
  onRowRestore: (rowId: string) => void;
  onRowAdd: (afterRowId: string) => void;
  onRowTypeChange: (rowId: string, value: 'detail' | 'note' | 'subtotal' | 'midtotal') => void;
  onRowSearch: (rowId: string) => void;
  onMatchOpen: (rowId: string) => void;
  onToggleOcrName: (rowId: string) => void;
  /** 見積: 未マッチ明細に個別で仮コード(99999950)を付与する */
  onRowSpotCode?: (rowId: string) => void;
  /** 明細行を手動で1つ上/下へ並べ替える。備考/小計行は対象外。 */
  onMoveRow?: (rowId: string, direction: 'up' | 'down') => void;
  /** 見積のコード一括登録で、チェックした明細のID（未指定ならチェック欄を出さない） */
  selectedRowIds?: Set<string>;
  onToggleSelect?: (rowIds: string[], selected: boolean) => void;
  /** ドラッグ&ドロップで、行を落とした先の明細行の位置へ動かす */
  onMoveRowTo?: (rowId: string, targetId: string) => void;
  /** 伝票分割: この行の次の行から次の伝票にする（「+」と同じく押した行の下に作用） */
  onSlipSplit?: (rowId: string) => void;
  /** 伝票分割: この行が先頭の伝票を1つ前の伝票に戻す（区切りの取り消し） */
  onSlipMerge?: (rowId: string) => void;
  /** 商品コードを直接入力してマスタ照合・確定する。 */
  onRowCodeInput?: (rowId: string, code: string) => void;
  ocrNameRows: Set<string>;
  /** 列ヘッダークリックで明細を並べ替える(ERP書込み画面と同じUX)。備考/小計行は固定。 */
  onSort?: (key: string) => void;
  sortKey?: string | null;
  sortAsc?: boolean;
  /** 見積(estimate)のとき true。見積では row_type 未設定行・手動追加行にも
   * 明細/備考/小計の区分セレクタを表示し、手動で分類できるようにする。 */
  isEstimate?: boolean;
  /** 届け先が「返品」(todoke_code=011)のとき true。個数を自動でマイナスにする。 */
  isReturn?: boolean;
  /** ERPへ書き込む商品コードを返す。OCR名採用時に汎用コードを維持するか仮コードへ倒すかの
   * 判定を含むため、画面側で組み直さず OcrPage の実装をそのまま使う（判定の一本化）。 */
  erpProductCode?: (row: Row) => string;
}

/** 返品時は絶対値にマイナスを付与（冪等）。それ以外はそのまま。空/非数はそのまま返す。 */
function applyReturnSign(n: number, isReturn: boolean): number {
  return isReturn ? -Math.abs(n) : n;
}

/** 見積の単価列(定価/見積単価/原価)の見出し。この表は列幅が内容に合わせて縮むため、
 * <col> の幅だけでは 20px 程度まで潰れて金額が読めなくなる（7桁が「1…」になる）。
 * 見出しに最小幅を持たせて、7桁の金額が収まる幅を確保する。 */
const PRICE_LABEL_CLASS = 'inline-block min-w-[64px] whitespace-nowrap';

function ResizeHandle({ thRef, tableRef }: { thRef: React.RefObject<HTMLTableCellElement | null>; tableRef: React.RefObject<HTMLTableElement | null> }) {
  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const th = thRef.current;
      const table = tableRef.current;
      if (!th || !table) return;

      // ドラッグ開始時に全列の幅をピクセルで固定
      const cols = table.querySelectorAll('colgroup col');
      const ths = table.querySelectorAll('thead th');
      ths.forEach((cell, i) => {
        const col = cols[i] as HTMLElement | undefined;
        if (col) col.style.width = `${(cell as HTMLElement).offsetWidth}px`;
      });

      const startX = e.clientX;
      const col = cols[Array.from(ths).indexOf(th)] as HTMLElement | undefined;
      const startW = th.offsetWidth;
      const startTableW = table.offsetWidth;

      const overlay = document.createElement('div');
      overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;cursor:col-resize;';
      document.body.appendChild(overlay);
      const onMove = (ev: MouseEvent) => {
        ev.preventDefault();
        const newW = Math.max(40, startW + ev.clientX - startX);
        if (col) col.style.width = `${newW}px`;
        table.style.width = `${startTableW + (newW - startW)}px`;
      };
      const onUp = () => {
        overlay.remove();
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [thRef, tableRef],
  );

  return (
    <span
      onMouseDown={onMouseDown}
      className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-primary/30 z-20"
    />
  );
}

/** セル内テキストを省略表示し、ホバーで全文をツールチップ表示 */
function TruncatedCell({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const text = typeof children === 'string' ? children : undefined;
  return (
    <div className={`overflow-hidden text-ellipsis whitespace-nowrap ${className}`} title={text}>
      {children}
    </div>
  );
}

export function RowTable({
  rows,
  matches,
  matchesOriginal,
  confirmedRows,
  isCellEdited,
  onCellEdit,
  onRowDelete,
  onRowRemove,
  onRowRestore,
  onRowAdd,
  onRowTypeChange,
  onRowSearch,
  onRowSpotCode,
  onMoveRow,
  onMoveRowTo,
  onSlipSplit,
  onSlipMerge,
  onRowCodeInput,
  onMatchOpen,
  onToggleOcrName,
  ocrNameRows,
  onSort,
  sortKey,
  sortAsc,
  isEstimate,
  isReturn = false,
  erpProductCode,
  selectedRowIds,
  onToggleSelect,
}: Props) {
  // 9列 + 見積の単価3列(定価/見積単価/原価)。見積でないときは後ろ3つは使わない。
  const thRefs = Array.from({ length: 12 }, () => useRef<HTMLTableCellElement>(null));
  const tableRef = useRef<HTMLTableElement>(null);
  // コード一括登録のチェック欄。対象は明細行（非削除）だけ。
  const selectableIds = selectedRowIds && onToggleSelect
    ? rows.filter((r) => (r.row_type ?? 'detail') === 'detail' && !r.isDeleted).map((r) => r.id)
    : [];
  const selectedCount = selectableIds.filter((id) => selectedRowIds?.has(id)).length;
  const allSelected = selectableIds.length > 0 && selectedCount === selectableIds.length;
  const someSelected = selectedCount > 0;
  // ドラッグ中の行と、いま重ねている行（落とす位置の線を出すため）。
  // HTML5 の draggable はテーブル行で掴める範囲が不安定なうえタッチで動かないため、
  // pointer イベントで掴み、指先の下にある行（data-drop-row）を落とし先にする。
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const overRef = useRef<string | null>(null);
  const rowIndex = (id: string | null) => (id ? rows.findIndex((x) => x.id === id) : -1);
  useEffect(() => {
    if (!dragId) return;
    const rowAt = (e: PointerEvent) =>
      (document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-drop-row]') as HTMLElement | null)
        ?.dataset.dropRow ?? null;
    const onMove = (e: PointerEvent) => {
      const id = rowAt(e);
      overRef.current = id && id !== dragId ? id : null;
      setOverId(overRef.current);
    };
    const onUp = () => {
      if (overRef.current) onMoveRowTo?.(dragId, overRef.current);
      overRef.current = null;
      setDragId(null);
      setOverId(null);
    };
    const prevSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';  // ドラッグ中に文字が選択されないようにする
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      document.body.style.userSelect = prevSelect;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragId, onMoveRowTo]);

  // コード直打ち中の行ID。普段は非表示で、商品名2をクリックした行だけ入力欄を表示する。
  const [codeEditId, setCodeEditId] = useState<string | null>(null);
  // ↑↓での入力欄フォーカス移動中フラグ（移動時に onBlur が入力欄を閉じないようにする）。
  const navigatingRef = useRef(false);

  /** コード入力を開ける隣の明細行(detail・非削除)のIDを返す。端なら null。 */
  const adjacentDetailId = (rowId: string, dir: 'up' | 'down'): string | null => {
    const detail = rows.filter((x) => (x.row_type ?? 'detail') === 'detail' && !x.isDeleted);
    const i = detail.findIndex((x) => x.id === rowId);
    if (i < 0) return null;
    const j = dir === 'up' ? i - 1 : i + 1;
    return j >= 0 && j < detail.length ? detail[j].id : null;
  };

  /** 商品名2 セル: 普段は name2 テキスト（クリックで商品コード直打ち入力に切替）。
   * 入力してEnter/フォーカスアウトでマスタ照合し、既存マッチも上書きする。
   * 入力中はコード順で前後の候補を表示し、クリックでもそのコードを確定できる。
   * ↑↓キーで隣の明細行の入力欄へフォーカス移動（並び替えはしない）。 */
  const renderName2Cell = (r: Row, name2: string) => {
    if (!onRowCodeInput || r.isDeleted) {
      return name2 ? (
        <span className="text-xs text-muted overflow-hidden text-ellipsis whitespace-nowrap" title={name2}>{name2}</span>
      ) : null;
    }
    if (codeEditId === r.id) {
      return (
        <CodeSuggestInput
          key={r.id}
          onArrow={(dir) => {
            const nextId = adjacentDetailId(r.id, dir);
            if (nextId) { navigatingRef.current = true; setCodeEditId(nextId); }
          }}
          onEnter={(code) => {
            onRowCodeInput(r.id, code);
            setCodeEditId(null);
          }}
          onBlurCommit={(code) => {
            if (code) onRowCodeInput(r.id, code);
            // ↑↓でのフォーカス移動中は閉じない（移動先の入力欄が既に開いている）。
            if (navigatingRef.current) { navigatingRef.current = false; return; }
            setCodeEditId(null);
          }}
        />
      );
    }
    return (
      <button
        type="button"
        onClick={() => setCodeEditId(r.id)}
        title="クリックで商品コードを直接入力"
        className="w-full text-left text-xs text-muted overflow-hidden text-ellipsis whitespace-nowrap hover:bg-slate-100 rounded px-0.5"
      >
        {name2 || <span className="text-slate-400">コード入力</span>}
      </button>
    );
  };

  // 並べ替え可能なヘッダーラベル。ResizeHandle(右端)とは別要素なので共存する。
  const sortMark = (key: string) => (onSort && sortKey === key ? (sortAsc ? ' ▲' : ' ▼') : '');
  const SortLabel = ({ k, children }: { k: string; children: React.ReactNode }) =>
    onSort ? (
      <button
        type="button"
        onClick={() => onSort(k)}
        className="hover:underline cursor-pointer"
        title="クリックで並べ替え"
      >
        {children}{sortMark(k)}
      </button>
    ) : (
      <>{children}</>
    );

  // 伝票分割。区切り行は伝票が変わる行の直前に出す（分割していなければ出さない）。
  const slips = slipInfo(rows);
  const colCount = isEstimate ? 12 : 9;
  // 行ID → 次に表示されている行のID（最後の行は無い）。区切りは押した行の下に入れる。
  const visibleIds = rows.filter((x) => !x.isDeleted).map((x) => x.id);
  const nextVisibleId: Record<string, string | undefined> = {};
  visibleIds.forEach((id, i) => { nextVisibleId[id] = visibleIds[i + 1]; });
  let prevSlip = 0;

  return (
    <>
    {slips.count > 1 && (
      <div className="mb-2 flex flex-wrap items-center gap-3 rounded border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm">
        <span className="rounded bg-amber-200 px-2 py-0.5 text-xs font-medium text-amber-900">{slips.count}伝票に分割</span>
        <span className="text-amber-900">
          {slips.sizes.map((n, i) => `伝票${i + 1}/${slips.count}: ${n}件`).join('　')}
        </span>
        <span className="text-success">
          合計 {slips.sizes.reduce((a, b) => a + b, 0)}件（ERPには伝票ごとに書き込み、伝票備考の先頭に「1/{slips.count}」のように付けます）
        </span>
        <span className="w-full text-amber-900">
          ※伝票を分割した場合、相手伝票No.は入力できず、ERPへも送りません（同じ番号の伝票が並ぶとERPで重複エラーになるため）
        </span>
      </div>
    )}
    <div className="overflow-auto max-h-[70vh]">
      {/* 見積は単価3列が増える。表を画面幅に押し込むと品名・商品名が潰れるので、
          狭い画面では横スクロールにして列幅を保つ。 */}
      <table ref={tableRef} className="text-sm w-full" style={isEstimate ? { minWidth: 960 } : undefined}>
        <colgroup>
          <col style={{ width: 46 }} />   {/* #（行番号＋並べ替え↑↓） */}
          <col style={{ width: '22%' }} />  {/* 品名/寸法 */}
          <col style={{ width: 44 }} />   {/* 操作 */}
          <col style={{ width: 26 }} />   {/* 単位 */}
          <col style={{ width: 40 }} />   {/* 入数 */}
          <col style={{ width: 48 }} />   {/* 個数 */}
          <col style={{ width: '30%' }} />  {/* 商品名1/2 */}
          <col style={{ width: '12%' }} />  {/* 分類 */}
          <col style={{ width: 64 }} />   {/* 備考(見積は備考1/2) */}
          {isEstimate && (
            <>
              {/* 他の列と同じくドラッグで幅を変えられる。初期幅は見出し側の最小幅(PRICE_LABEL_CLASS)で確保する */}
              <col style={{ width: 72 }} />   {/* 定価 */}
              <col style={{ width: 72 }} />   {/* 見積単価 */}
              <col style={{ width: 72 }} />   {/* 原価 */}
            </>
          )}
        </colgroup>
        {/* border-collapse の継ぎ目に背景が無いと、スクロールした明細が
            ヘッダーの2段の隙間から透けて見える。thead にも th と同じ色を敷く。 */}
        <thead className="sticky top-0 z-10 bg-slate-50">
          <tr>
            <th rowSpan={2} ref={thRefs[0]} className="px-1 relative overflow-hidden">
              {selectableIds.length > 0 && (
                <input
                  type="checkbox"
                  className="mr-1 align-middle"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={(e) => onToggleSelect?.(selectableIds, e.target.checked)}
                  title="明細をすべて選ぶ／外す"
                  aria-label="明細をすべて選ぶ"
                />
              )}
              <SortLabel k="line_no">#</SortLabel><ResizeHandle thRef={thRefs[0]} tableRef={tableRef} /></th>
            <th ref={thRefs[1]} className="relative overflow-hidden border-b border-border"><SortLabel k="product_name">品名</SortLabel><ResizeHandle thRef={thRefs[1]} tableRef={tableRef} /></th>
            <th rowSpan={2} ref={thRefs[2]} className="px-1 relative overflow-hidden"></th>
            <th rowSpan={2} ref={thRefs[3]} className="px-1 relative overflow-hidden">単位<ResizeHandle thRef={thRefs[3]} tableRef={tableRef} /></th>
            <th ref={thRefs[4]} className="px-1 relative overflow-hidden text-xs text-right border-b border-border">入数<ResizeHandle thRef={thRefs[4]} tableRef={tableRef} /></th>
            <th ref={thRefs[5]} className="px-1 relative overflow-hidden text-xs text-right border-b border-border">個数<ResizeHandle thRef={thRefs[5]} tableRef={tableRef} /></th>
            <th ref={thRefs[6]} className="relative overflow-hidden border-b border-border"><SortLabel k="matched_name1">商品名1</SortLabel><ResizeHandle thRef={thRefs[6]} tableRef={tableRef} /></th>
            <th rowSpan={2} ref={thRefs[7]} className="px-1 relative overflow-hidden">
              {/* セルが2段（上=分類 / 下=ERPへ書き込む商品コード）なのでラベルも2段にする */}
              <span className="flex flex-col leading-tight text-xs">
                <span className="whitespace-nowrap">分類</span>
                <span className="border-t border-border text-muted whitespace-nowrap"><SortLabel k="product_code">商品コード</SortLabel></span>
              </span>
              <ResizeHandle thRef={thRefs[7]} tableRef={tableRef} />
            </th>
            <th rowSpan={2} ref={thRefs[8]} className="relative overflow-hidden">
              {/* 明細備考1/2。JDNRWK/EDNRWKとも LINCMA/LINCMB を持つため注文・見積の両方で2段表示 */}
              <span className="flex flex-col leading-tight text-xs">
                <span className="whitespace-nowrap">備考1</span>
                <span className="border-t border-border text-muted whitespace-nowrap">備考2</span>
              </span>
              <ResizeHandle thRef={thRefs[8]} tableRef={tableRef} />
            </th>
            {isEstimate && (
              <>
                <th rowSpan={2} ref={thRefs[9]} className="px-1 relative overflow-hidden text-xs text-right"><span className={PRICE_LABEL_CLASS}>定価</span><ResizeHandle thRef={thRefs[9]} tableRef={tableRef} /></th>
                <th rowSpan={2} ref={thRefs[10]} className="px-1 relative overflow-hidden text-xs text-right"><span className={PRICE_LABEL_CLASS}>見積単価</span><ResizeHandle thRef={thRefs[10]} tableRef={tableRef} /></th>
                <th rowSpan={2} ref={thRefs[11]} className="px-1 relative overflow-hidden text-xs text-right"><span className={PRICE_LABEL_CLASS}>原価</span><ResizeHandle thRef={thRefs[11]} tableRef={tableRef} /></th>
              </>
            )}
          </tr>
          <tr>
            <th className="relative overflow-hidden text-xs">寸法</th>
            <th colSpan={2} className="px-1 relative overflow-hidden text-xs text-right">数量</th>
            <th className="relative overflow-hidden text-xs">商品名2</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const match = matches[r.id];
            const confirmed = confirmedRows.has(r.id);
            const stripe = !r.isDeleted && !r.isAdded && idx % 2 === 1 ? 'bg-slate-50' : '';
            const useOcr = ocrNameRows.has(r.id);
            const rowType = r.row_type ?? 'detail';
            const isDetail = rowType === 'detail';   // 備考(note)/小計(subtotal)/中計(midtotal)は商品なし
            const isNote = rowType === 'note';       // 備考は数量系(入数/個数/数量)を持たない
            // 小計(4)・中計(5)は金額の集計行。商品名の手入力は不可(読み取り専用)。
            const isSubtotal = rowType === 'subtotal' || rowType === 'midtotal';
            const origMatch = matchesOriginal[r.id];
            const isMatchChanged = match && origMatch && match.matchedId !== origMatch.matchedId;
            const rowClass = `${r.isDeleted ? 'opacity-40 line-through' : ''} ${
              !confirmed ? 'bg-amber-50' : stripe
            } ${r.isAdded ? 'bg-blue-50' : ''}`;
            const slipNo = slips.groupOf[r.id];
            const slipStarts = !r.isDeleted && slips.count > 1 && slipNo !== prevSlip;
            if (!r.isDeleted) prevSlip = slipNo;
            // 最後の行と、すでに下に区切りがある行（伝票の最後の行）には区切りを入れられない
            const nextId = nextVisibleId[r.id];
            const canSplit = !!onSlipSplit && !r.isDeleted && !!nextId
              && slips.groupOf[nextId] === slipNo;
            // 明細行（非削除）だけがドラッグの対象・落とし先になる（備考/小計は位置固定）
            const canDrag = !!onMoveRowTo && isDetail && !r.isDeleted;
            const isDropTarget = canDrag && !!dragId && overId === r.id && dragId !== r.id;
            // 下へ動かすときは行の下、上へ動かすときは行の上に線を出す（落ちる位置と一致させる）
            const dropBelow = isDropTarget && rowIndex(dragId) < idx;
            const dropProps = canDrag ? { 'data-drop-row': r.id } : {};
            return (
              <React.Fragment key={r.id}>
              {slipStarts && (
                <tr className="bg-amber-50 border-t-2 border-dashed border-amber-400">
                  <td colSpan={colCount} className="px-2 py-1 text-xs text-amber-900">
                    <span className="font-medium">伝票 {slipNo}/{slips.count}</span>
                    <span className="ml-2">{slips.sizes[slipNo - 1]}件</span>
                    {slipNo > 1 && onSlipMerge && (
                      <button
                        type="button"
                        onClick={() => onSlipMerge(r.id)}
                        title="この区切りを取り消して、前の伝票とまとめる"
                        aria-label="区切りを取り消す"
                        className="float-right rounded px-1.5 text-amber-900 hover:bg-amber-200"
                      >
                        ✕ 区切りを取り消す
                      </button>
                    )}
                  </td>
                </tr>
              )}
              <tr
                className={`group ${rowClass} ${dragId === r.id ? 'opacity-50' : ''} ${
                  isDropTarget && !dropBelow ? 'border-t-2 border-t-primary' : ''}`}
                {...dropProps}
              >
                <td rowSpan={2} className="overflow-hidden align-middle p-0">
                  <div className="flex items-center justify-center gap-0.5">
                    {selectableIds.length > 0 && isDetail && !r.isDeleted && (
                      <input
                        type="checkbox"
                        checked={!!selectedRowIds?.has(r.id)}
                        onChange={(e) => onToggleSelect?.([r.id], e.target.checked)}
                        aria-label={`${r.line_no ?? ''}行目を選ぶ`}
                      />
                    )}
                    {canDrag && (
                      <span
                        onPointerDown={(e) => {
                          if (e.button !== 0) return;
                          e.preventDefault();
                          setDragId(r.id);
                        }}
                        title="ドラッグして行を移動"
                        aria-label="ドラッグして行を移動"
                        className="cursor-grab active:cursor-grabbing select-none touch-none text-slate-400 hover:text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ⠿
                      </span>
                    )}
                    <span>{r.line_no ?? ''}</span>
                    {onMoveRow && isDetail && !r.isDeleted && (
                      <div className="flex flex-col items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                        <button
                          onClick={() => onMoveRow(r.id, 'up')}
                          title="上へ移動"
                          aria-label="上へ移動"
                          className="inline-flex items-center justify-center w-4 h-3.5 rounded text-[10px] leading-none text-slate-500 border border-slate-300 hover:bg-slate-200 transition-colors"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => onMoveRow(r.id, 'down')}
                          title="下へ移動"
                          aria-label="下へ移動"
                          className="inline-flex items-center justify-center w-4 h-3.5 rounded text-[10px] leading-none text-slate-500 border border-slate-300 hover:bg-slate-200 transition-colors"
                        >
                          ↓
                        </button>
                      </div>
                    )}
                  </div>
                </td>
                <td rowSpan={2} className="overflow-hidden align-middle p-0">
                  <div className="flex items-center gap-1">
                    {(isEstimate || r.row_type) && (
                      <select
                        value={rowType}
                        onChange={(e) => onRowTypeChange(r.id, e.target.value as 'detail' | 'note' | 'subtotal' | 'midtotal')}
                        disabled={r.isDeleted}
                        title="見積設定区分 (0明細/3備考/4小計/5中計)"
                        className={`shrink-0 text-[10px] px-0.5 rounded border-0 cursor-pointer disabled:cursor-default ${
                          rowType === 'note'
                            ? 'bg-blue-100 text-blue-700'
                            : rowType === 'subtotal' || rowType === 'midtotal'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        <option value="detail">明細</option>
                        <option value="note">備考</option>
                        <option value="subtotal">小計</option>
                        <option value="midtotal">中計</option>
                      </select>
                    )}
                    {r.isDeleted ? (
                      <TruncatedCell>{r.product_name}</TruncatedCell>
                    ) : (
                      <EditableField
                        value={r.product_name}
                        onChange={(v) => onCellEdit(r.id, 'product_name', v)}
                        isEdited={isCellEdited(r.id, 'product_name')}
                        autoFocus={r.isAdded && !r.product_name}
                        className="w-full text-xs"
                      />
                    )}
                  </div>
                  <div className="border-t border-border">
                    {r.isDeleted ? (
                      <TruncatedCell className="text-xs text-muted">{r.size}</TruncatedCell>
                    ) : (
                      <EditableField
                        value={r.size}
                        onChange={(v) => onCellEdit(r.id, 'size', v)}
                        isEdited={isCellEdited(r.id, 'size')}
                        className="w-full text-xs"
                      />
                    )}
                  </div>
                </td>
                <td rowSpan={2} className="align-middle p-0 text-center">
                  {r.isDeleted ? (
                    // inline-flex + no-underline で、行の line-through がボタンに伝播しないようにする
                    <div className="inline-flex items-center justify-center gap-1 no-underline">
                      <button onClick={() => onRowRestore(r.id)} className="text-xs text-primary no-underline hover:underline">戻</button>
                      <button onClick={() => onRowRemove(r.id)} className="text-xs text-danger no-underline hover:underline">×</button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-0.5">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => onRowAdd(r.id)}
                          title="下に行を追加"
                          aria-label="行を追加"
                          className="inline-flex items-center justify-center w-6 h-6 rounded text-lg font-bold leading-none text-success border border-success/40 hover:bg-success hover:text-white transition-colors"
                        >
                          +
                        </button>
                        <button
                          onClick={() => onRowDelete(r.id)}
                          title="この行を削除"
                          aria-label="行を削除"
                          className="inline-flex items-center justify-center w-6 h-6 rounded text-lg font-bold leading-none text-danger border border-danger/40 hover:bg-danger hover:text-white transition-colors"
                        >
                          −
                        </button>
                      </div>
                      <div className="flex items-center justify-center gap-1">
                        {isDetail && (
                          <button
                            onClick={() => onRowSearch(r.id)}
                            title="商品を検索"
                            aria-label="商品を検索"
                            className="inline-flex items-center justify-center w-6 h-5 rounded text-sm text-primary border border-primary/30 hover:bg-primary/10 transition-colors"
                          >
                            🔍
                          </button>
                        )}
                        {canSplit && (
                          <button
                            onClick={() => onSlipSplit!(r.id)}
                            title="次の行から次の伝票にする"
                            aria-label="次の行から次の伝票にする"
                            className="inline-flex items-center justify-center w-6 h-5 rounded text-sm text-amber-700 border border-amber-400/60 hover:bg-amber-100 transition-colors"
                          >
                            ✂
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </td>
                <td rowSpan={2} className="overflow-hidden align-middle p-0">
                  {r.isDeleted ? (
                    <TruncatedCell>{r.unit}</TruncatedCell>
                  ) : (
                    <EditableField
                      value={r.unit}
                      onChange={(v) => onCellEdit(r.id, 'unit', v)}
                      isEdited={isCellEdited(r.id, 'unit')}
                      className="w-full text-right text-xs"
                    />
                  )}
                </td>
                <td className="overflow-hidden p-0">
                  {isNote ? null : r.isDeleted ? (
                    <TruncatedCell>{r.irisu || '1'}</TruncatedCell>
                  ) : (
                    <EditableField
                      value={r.irisu ?? '1'}
                      onChange={(v) => {
                        onCellEdit(r.id, 'irisu', v);
                        // 個数は符号付きのまま、入数は係数（正）として数量を再計算
                        const kosu = parseInt(r.kosu ?? r.quantity, 10) || 0;
                        const iri = parseInt(v, 10) || 1;
                        onCellEdit(r.id, 'quantity', String(kosu * iri));
                      }}
                      isEdited={isCellEdited(r.id, 'irisu')}
                      className="w-full text-right text-xs"
                    />
                  )}
                </td>
                <td className="overflow-hidden p-0">
                  {isNote ? null : r.isDeleted ? (
                    <TruncatedCell>{r.kosu || r.quantity}</TruncatedCell>
                  ) : (
                    <EditableField
                      value={r.kosu ?? r.quantity}
                      onChange={(v) => {
                        const parsed = parseInt(v, 10);
                        // 返品時は入力値を自動でマイナスに（例: 5→-5、-5→-5）。非数はそのまま。
                        const kosu = isNaN(parsed) ? 0 : applyReturnSign(parsed, isReturn);
                        onCellEdit(r.id, 'kosu', isNaN(parsed) ? v : String(kosu));
                        const iri = parseInt(r.irisu ?? '1', 10) || 1;
                        onCellEdit(r.id, 'quantity', String(kosu * iri));
                      }}
                      isEdited={isCellEdited(r.id, 'kosu')}
                      className="w-full text-right text-xs"
                    />
                  )}
                </td>
                <td rowSpan={2} className={`text-xs overflow-hidden align-middle ${isMatchChanged ? 'bg-amber-50' : ''}`}>
                  {!isDetail ? (
                    // 備考行: 商品名1/2を手入力可。ERPへ書かれるのは商品名1/2なので
                    // 品名/寸法(OCR参考値)とは別に編集できる。「品名」ボタンで品名/寸法を反映。
                    // 小計行: 顧客要望により手入力不可(読み取り専用)。
                    r.isDeleted || isSubtotal ? (
                      <TruncatedCell>{subtotalLabelOf(rowType, r.note_name1 || r.product_name) ?? (r.note_name1 || r.product_name)}</TruncatedCell>
                    ) : (
                      <>
                        <div className="flex items-center gap-1 min-w-0">
                          <EditableField
                            value={r.note_name1 || r.product_name}
                            onChange={(v) => onCellEdit(r.id, 'note_name1', v)}
                            className="w-full text-xs"
                          />
                          <button
                            onClick={() => { onCellEdit(r.id, 'note_name1', r.product_name); onCellEdit(r.id, 'note_name2', r.size); }}
                            title="品名/寸法を商品名1/2に反映"
                            aria-label="品名/寸法を商品名1/2に反映"
                            className="shrink-0 text-xs px-1 py-0.5 rounded border bg-slate-100 border-slate-300 text-slate-600 hover:bg-slate-200"
                          >
                            品名
                          </button>
                        </div>
                        <div className="border-t border-border">
                          <EditableField
                            value={r.note_name2 ?? r.size}
                            onChange={(v) => onCellEdit(r.id, 'note_name2', v)}
                            className="w-full text-xs"
                          />
                        </div>
                      </>
                    )
                  ) : match ? (
                    <>
                      <div className="flex items-center gap-1 min-w-0">
                        <button
                          onClick={() => onMatchOpen(r.id)}
                          className={`text-left text-xs overflow-hidden text-ellipsis whitespace-nowrap hover:underline ${isMatchChanged ? 'text-amber-700 font-medium' : useOcr ? 'text-amber-700' : 'text-primary'}`}
                          title={`${useOcr ? r.product_name : match.matchedName} (${useOcr ? SPOT_PRODUCT_CODE : match.matchedId})`}
                        >
                          {useOcr ? r.product_name : match.matchedName}
                        </button>
                        <button
                          onClick={() => onToggleOcrName(r.id)}
                          title={useOcr ? 'マスタ商品名に戻す' : 'OCR品名+寸法を使う'}
                          className={`shrink-0 text-xs px-1 py-0.5 rounded border ${
                            useOcr
                              ? 'bg-amber-100 border-amber-300 text-amber-800'
                              : 'bg-slate-100 border-slate-300 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {useOcr ? 'OCR' : 'ﾏｽﾀ'}
                        </button>
                      </div>
                      {/* 商品名2 行: 普段は name2 テキスト。クリックで商品コード直打ち（既存マッチも上書き可） */}
                      <div className="border-t border-border min-w-0">
                        {renderName2Cell(r, useOcr ? (r.size ?? '') : (match.candidates.find((c) => c.id === match.matchedId)?.name2 ?? ''))}
                      </div>
                      {useOcr && !match.candidates.find((c) => c.id === match.matchedId)?.name_editable && (
                        <div
                          className="text-[10px] text-amber-700 overflow-hidden text-ellipsis whitespace-nowrap"
                          title={`OCR読取テキストを採用したため商品コードを汎用コード(${SPOT_PRODUCT_CODE})に設定しました。該当する汎用コードがあれば検索して設定してください。`}
                        >
                          汎用コード({SPOT_PRODUCT_CODE})／要確認
                        </div>
                      )}
                    </>
                  ) : r.isDeleted ? (
                    '-'
                  ) : (
                    <>
                      {/* 商品名1 行: 未マッチ表示＋（見積のみ）仮コード。検索🔍は操作列に集約。 */}
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted">-</span>
                        {isEstimate && onRowSpotCode && (
                          <button
                            onClick={() => onRowSpotCode(r.id)}
                            title="仮コード(99999950)を付与"
                            aria-label="仮コード(99999950)を付与"
                            className="shrink-0 text-xs px-1 py-0.5 rounded border bg-slate-100 border-slate-300 text-slate-600 hover:bg-slate-200"
                          >
                            仮
                          </button>
                        )}
                      </div>
                      {/* 商品名2 行: クリックで商品コード直打ち */}
                      <div className="border-t border-border min-w-0">
                        {renderName2Cell(r, '')}
                      </div>
                    </>
                  )}
                </td>
                <td rowSpan={2} className="text-xs text-muted overflow-hidden align-middle p-0">
                  <TruncatedCell>
                    {!isDetail ? '' : match ? (match.candidates.find((c) => c.id === match.matchedId)?.category || '-') : '-'}
                  </TruncatedCell>
                  {/* 分類の下段にERPへ書き込む商品コードを出す。コードを見ながら入力する
                      運用のため（門田氏要望）。マッチしていない行・明細以外の行は空。 */}
                  <TruncatedCell className="border-t border-border font-mono">
                    {isDetail ? (erpProductCode?.(r) || '') : ''}
                  </TruncatedCell>
                </td>
                <td rowSpan={2} className="overflow-hidden align-middle p-0">
                  {r.isDeleted ? (
                    <>
                      <TruncatedCell>{r.item_note || ''}</TruncatedCell>
                      <TruncatedCell className="border-t border-border text-xs text-muted">{r.item_note2 || ''}</TruncatedCell>
                    </>
                  ) : (
                    <>
                      <EditableField
                        value={r.item_note ?? ''}
                        onChange={(v) => onCellEdit(r.id, 'item_note', v)}
                        isEdited={isCellEdited(r.id, 'item_note')}
                        className="w-full"
                      />
                      <div className="border-t border-border">
                        <EditableField
                          value={r.item_note2 ?? ''}
                          onChange={(v) => onCellEdit(r.id, 'item_note2', v)}
                          isEdited={isCellEdited(r.id, 'item_note2')}
                          className="w-full text-xs"
                        />
                      </div>
                    </>
                  )}
                </td>
                {isEstimate && ([
                  ['list_price', r.list_price] as const,
                  ['estimate_unit_price', r.estimate_unit_price] as const,
                  ['cost_price', r.cost_price] as const,
                ]).map(([field, val]) => (
                  <td key={field} rowSpan={2} className="overflow-hidden align-middle p-0">
                    {r.isDeleted ? (
                      <TruncatedCell className="text-xs text-right">{val || ''}</TruncatedCell>
                    ) : (
                      <EditableField
                        value={val ?? ''}
                        onChange={(v) => onCellEdit(r.id, field, v)}
                        isEdited={isCellEdited(r.id, field)}
                        className="w-full text-xs text-right"
                        navGroup={`price:${field}`}
                      />
                    )}
                  </td>
                ))}
              </tr>
              <tr
                className={`${rowClass} ${dragId === r.id ? 'opacity-50' : ''} ${
                  dropBelow ? 'border-b-2 border-b-primary' : ''}`}
                {...dropProps}
              >
                <td colSpan={2} className="text-right text-xs font-mono p-0 pr-1">
                  {isNote ? '' : r.quantity}
                </td>
              </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}
